"use client";

import React, { useState, useCallback, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ConfirmDialogOptions {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
}

interface DialogState extends ConfirmDialogOptions {
  open: boolean;
}

type Resolver = (value: boolean) => void;

let showDialog: ((options: ConfirmDialogOptions) => Promise<boolean>) | null =
  null;

/**
 * Imperative confirm dialog — call from anywhere, no hooks needed.
 *
 * Usage:
 *   import { confirmDialog } from "@/lib/dialogs";
 *   if (await confirmDialog({ title: "Delete?", description: "This cannot be undone." })) { ... }
 */
export async function confirmDialog(
  options: ConfirmDialogOptions
): Promise<boolean> {
  if (!showDialog) {
    // Provider not mounted — fall back to native confirm so the app never breaks
    return window.confirm(options.description);
  }
  return showDialog(options);
}

/**
 * Mount once in layout.tsx — renders the shared dialog instance.
 */
export function ConfirmDialogProvider() {
  const [state, setState] = useState<DialogState>({
    open: false,
    title: "",
    description: "",
  });
  const resolverRef = useRef<Resolver | null>(null);

  const show = useCallback(
    (options: ConfirmDialogOptions): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setState({ ...options, open: true });
      });
    },
    []
  );

  // Register the singleton
  React.useEffect(() => {
    showDialog = show;
    return () => {
      showDialog = null;
    };
  }, [show]);

  const close = useCallback((result: boolean) => {
    setState((prev) => ({ ...prev, open: false }));
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  return (
    <Dialog
      open={state.open}
      onOpenChange={(open) => {
        if (!open) close(false);
      }}
    >
      <DialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
        <DialogOverlay className="z-[100200]" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-[100201] grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border border-slate-700 bg-[rgb(24,28,36)] p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-sm text-white [animation-fill-mode:backwards]"
          )}
        >
          <DialogHeader>
            <DialogTitle>{state.title}</DialogTitle>
            <DialogDescription>{state.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => close(false)}>
              {state.cancelText || "Cancel"}
            </Button>
            <Button
              variant={state.variant === "destructive" ? "destructive" : "default"}
              onClick={() => close(true)}
            >
              {state.confirmText || "Confirm"}
            </Button>
          </DialogFooter>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
